import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import qs from "query-string";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FieldSchema<T = any> = {
    defaultValue: T;
    parse: (raw: string) => T | null;
    serialize: (value: T) => string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Schema = Record<string, FieldSchema<any>>;

type StateFromSchema<S extends Schema> = {
    [K in keyof S]: S[K] extends FieldSchema<infer T> ? T : never;
};

function useUrlState<S extends Schema>(
    schema: S,
): [StateFromSchema<S>, (updates: Partial<StateFromSchema<S>>) => void] {
    const history = useHistory();
    const { search } = useLocation();

    const parsed = qs.parse(search);
    const state = {} as StateFromSchema<S>;

    for (const key in schema) {
        const { defaultValue, parse } = schema[key];
        const raw = parsed[key];

        if (raw === null || raw === undefined || Array.isArray(raw)) {
            state[key] = defaultValue;
        } else {
            const result = parse(raw);
            if (result === null) {
                if (process.env.NODE_ENV === "development") {
                    console.warn(
                        `useUrlState: failed to parse param "${key}" with value "${raw}", using default`,
                    );
                }
                state[key] = defaultValue;
            } else {
                state[key] = result;
            }
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const setState = React.useCallback((updates: Partial<StateFromSchema<S>>) => {
        const current = qs.parse(history.location.search);
        const next = { ...current };

        for (const key in updates) {
            const value = updates[key];
            if (value === undefined) continue;
            const { serialize, defaultValue } = schema[key];
            if (value === defaultValue) {
                delete next[key];
            } else {
                next[key] = serialize(value);
            }
        }

        history.replace({ search: qs.stringify(next) });
    }, [history, schema]);

    return [state, setState];
}

export default useUrlState;
